import React, { useState, useEffect } from 'react';
import Container from '../common/Container';
import Header from '../layout/Header';
import PictureOption from '../game/PictureOption';
import FeedbackMessage from '../game/FeedbackMessage';
import ChallengeCompletedPopup from '../game/ChallengeCompletedPopup';
import LoadingSpinner from '../common/LoadingSpinner';
import { PicturePair } from '../../types';

interface PictureGameScreenProps {
    pair: PicturePair | null;
    currentRound: number;
    totalRounds: number;
    score: number;
    onMakeSelection: (selectedImageId: string, isAI: boolean) => Promise<void>;
    isLoading: boolean;
    requiredCorrectAnswers?: number;
    showChallengeCompletedPopup?: boolean;
    onContinueChallenge?: () => void;
    onFinishGame?: () => void;
    onBackToMenu?: () => void;
}

const PictureGameScreen: React.FC<PictureGameScreenProps> = ({
    pair,
    currentRound,
    totalRounds,
    score,
    onMakeSelection,
    isLoading,
    requiredCorrectAnswers = 8,
    showChallengeCompletedPopup = false,
    onContinueChallenge,
    onFinishGame,
    onBackToMenu,
}) => {
    const [showFeedback, setShowFeedback] = useState(false);
    const [showFeedbackMessage, setShowFeedbackMessage] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [aiOnLeft, setAiOnLeft] = useState(false);

    useEffect(() => {
        setAiOnLeft(Math.random() < 0.5);

        // Clear any active focus states when content changes
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }, [pair]);

    const handleSelection = async (position: 'left' | 'right') => {
        if (showFeedback || !pair || isLoading) return;

        // Determine which image is which based on aiOnLeft
        const leftImage = aiOnLeft ? pair.images.find(img => img.isAI) : pair.images.find(img => !img.isAI);
        const rightImage = aiOnLeft ? pair.images.find(img => !img.isAI) : pair.images.find(img => img.isAI);

        const selectedImage = position === 'left' ? leftImage : rightImage;

        if (!selectedImage) return;

        const selectedAI = selectedImage.isAI;

        setIsCorrect(selectedAI);
        setFeedbackMessage(selectedAI ? 'Correct! That was AI-generated.' : 'Incorrect! That was a real photo.');
        setShowFeedback(true);
        setShowFeedbackMessage(true);

        setTimeout(async () => {
            setShowFeedbackMessage(false);
            setShowFeedback(false);

            // Make the selection
            await onMakeSelection(selectedImage.id, selectedAI);
        }, 1500);
    };

    if (!pair) {
        return (
            <div className="min-h-screen bg-dark-950 flex items-center justify-center relative overflow-hidden px-4">
                {/* Animated background */}
                <div className="absolute inset-0 bg-circuit opacity-5"></div>
                <div className="absolute top-1/4 left-1/4 w-48 h-48 sm:w-96 sm:h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow"></div>

                <div className="text-center relative z-10">
                    {isLoading ? (
                        <LoadingSpinner size="medium" message="Loading Visual Data..." />
                    ) : (
                        <div className="max-w-md mx-auto bg-dark-900/80 border border-red-500/30 rounded-xl p-6 backdrop-blur-sm">
                            <div className="text-4xl mb-4">⚠️</div>
                            <h3 className="text-xl font-bold text-red-400 mb-2">Connection Error</h3>
                            <p className="text-dark-300 mb-6">
                                We couldn't load the visual challenge data. This might be due to a server connection issue.
                            </p>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-6 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 rounded-lg transition-colors font-mono text-sm uppercase tracking-wider"
                            >
                                Reload Page
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Helper to get images for display
    const getLeftImage = () => aiOnLeft ? pair.images.find(img => img.isAI) : pair.images.find(img => !img.isAI);
    const getRightImage = () => aiOnLeft ? pair.images.find(img => !img.isAI) : pair.images.find(img => img.isAI);

    const leftImage = getLeftImage();
    const rightImage = getRightImage();

    if (!leftImage || !rightImage) return null;

    return (
        <div className="min-h-screen bg-dark-950 relative">
            {/* Animated background elements */}
            <div className="absolute inset-0 bg-circuit opacity-5"></div>
            <div className="absolute top-0 right-1/4 w-48 h-48 sm:w-96 sm:h-96 bg-primary-500/5 rounded-full blur-3xl animate-pulse-slow"></div>
            <div className="absolute bottom-0 left-1/4 w-48 h-48 sm:w-96 sm:h-96 bg-secondary-500/5 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>

            <Header title="AI or Real?" showScore={false} score={score} mode="picture" onBack={onBackToMenu} />

            <Container className="relative z-10 px-4 sm:px-6">
                {/* Sticky Progress Display */}
                <div className="sticky top-0 z-20 bg-dark-950/95 backdrop-blur-sm border-b border-primary-500/30 shadow-lg py-3 sm:py-4 -mx-4 sm:-mx-6 px-4 sm:px-6 mb-4 sm:mb-6">
                    <div className="bg-gradient-to-br from-dark-800 to-dark-900 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg max-w-md mx-auto">
                        <div className="flex flex-col xs:flex-row items-center justify-center space-y-2 xs:space-y-0 xs:space-x-4 sm:space-x-6 text-dark-300">
                            <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-primary-500 rounded-full"></div>
                                <span className="font-mono text-xs sm:text-sm">Round {currentRound}/{totalRounds}</span>
                            </div>
                            <div className="hidden xs:block w-px h-4 bg-dark-600"></div>
                            <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 sm:w-3 sm:h-3 bg-accent-500 rounded-full"></div>
                                <span className="font-mono text-xs sm:text-sm">
                                    Need {Math.max(0, requiredCorrectAnswers - score)} more to win
                                </span>
                            </div>
                        </div>

                        {/* Progress bar */}
                        <div className="mt-3 sm:mt-4 bg-dark-700 rounded-full h-2 overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-500 ease-out"
                                style={{ width: `${(currentRound / totalRounds) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
                {/* Challenge Header */}
                <div className="text-center mb-6 sm:mb-8 mt-4 sm:mt-6">
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400 tracking-wider uppercase mb-2">
                        Identify the AI Image
                    </h2>
                </div>

                {isLoading ? (
                    <div className="text-center py-8 sm:py-12">
                        <LoadingSpinner size="medium" message="Processing Next Challenge..." />
                    </div>
                ) : (
                    <div className="relative">
                        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
                            <div className="flex-1">
                                <PictureOption
                                    key={`left-${leftImage.id}-${currentRound}`}
                                    imageUrl={leftImage.imageUrl}
                                    isAI={leftImage.isAI}
                                    position="left"
                                    onSelect={() => handleSelection('left')}
                                    disabled={showFeedback}
                                />
                            </div>

                            <div className="flex-1">
                                <PictureOption
                                    key={`right-${rightImage.id}-${currentRound}`}
                                    imageUrl={rightImage.imageUrl}
                                    isAI={rightImage.isAI}
                                    position="right"
                                    onSelect={() => handleSelection('right')}
                                    disabled={showFeedback}
                                />
                            </div>
                        </div>

                        {/* Feedback message overlay */}
                        {showFeedbackMessage && (
                            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                <FeedbackMessage
                                    isCorrect={isCorrect}
                                    message={feedbackMessage}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Challenge Completed Popup */}
                {showChallengeCompletedPopup && onContinueChallenge && onFinishGame && (
                    <ChallengeCompletedPopup
                        score={score}
                        requiredCorrectAnswers={requiredCorrectAnswers}
                        currentRound={currentRound}
                        totalRounds={totalRounds}
                        onContinue={onContinueChallenge}
                        onViewResults={onFinishGame}
                    />
                )}
            </Container>
        </div>
    );
};

export default PictureGameScreen;
